/**
 * GrnamtNoticeBox.tsx
 * ------------------------------------------------------------
 * - 교부금 하달(미확인) 알림 표시
 */

import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import Button from "@/js/common/ui/Button";

export type GrnamtPrefill = {
  dpstDt: string; // 수입일자(YYYYMMDD)
  kwanCd: string;
  hangCd: string;
  mokCd: string;
  amount: string; // 콤마 포함 문자열 OK
  summary: string;
  memo: string;
};

type NoticeKey = {
  acntgYear: string;
  acntgMonth: string;
  implDt: string; // 화면용 YYYY-MM-DD
  grnamtOrgCd: string;

  bgtSbjct1stCd: string;
  bgtSbjct2stCd: string;
  bgtSbjct3stCd: string;
};

type NoticeRow = NoticeKey & {
  bgtSbjct1stNm?: string;
  bgtSbjct2stNm?: string;
  bgtSbjct3stNm?: string;

  aprvAmt: any;
};

interface Props {
  acntgYear: string;
  ognzCd: string;
  onPick: (prefill: GrnamtPrefill) => void;
}

const GrnamtNoticeBox = ({ acntgYear, ognzCd, onPick }: Props) => {
  const [list, setList] = React.useState<NoticeRow[]>([]);
  const [open, setOpen] = React.useState(false);
  const [callTime, setCallTime] = React.useState<Date>(new Date());

  const [hoverIdx, setHoverIdx] = React.useState<number | null>(null);

  // 확인 처리 중인 row 식별 (7개 키로만)
  const [confirmingKey, setConfirmingKey] = React.useState<NoticeKey | null>(null);

  const toYmd = (v: any) => {
    const s = String(v || "").trim();
    if (!s) return "";
    if (s.includes("-")) return s; // YYYY-MM-DD
    if (s.length === 8) return `${s.substring(0, 4)}-${s.substring(4, 6)}-${s.substring(6, 8)}`;
    return s;
  };

  const toMonthLabel = (m: any) => {
    const mm = String(m || "").trim();
    if (!mm) return "";
    const n = Number(mm);
    if (!Number.isNaN(n) && n >= 1 && n <= 12) return String(n); // "02" -> "2"
    return mm;
  };

  const isSameKey = (a: NoticeKey, b: NoticeKey) =>
    a.acntgYear === b.acntgYear &&
    a.acntgMonth === b.acntgMonth &&
    a.implDt === b.implDt &&
    a.grnamtOrgCd === b.grnamtOrgCd &&
    a.bgtSbjct1stCd === b.bgtSbjct1stCd &&
    a.bgtSbjct2stCd === b.bgtSbjct2stCd &&
    a.bgtSbjct3stCd === b.bgtSbjct3stCd;

  const pickKeyFromRow = (row: NoticeRow): NoticeKey => ({
    acntgYear: row.acntgYear,
    acntgMonth: row.acntgMonth,
    implDt: row.implDt,
    grnamtOrgCd: row.grnamtOrgCd,
    bgtSbjct1stCd: row.bgtSbjct1stCd,
    bgtSbjct2stCd: row.bgtSbjct2stCd,
    bgtSbjct3stCd: row.bgtSbjct3stCd,
  });

  // =========================
  // 조회 (미확인 하달 알림)
  // =========================
  React.useEffect(() => {
    if (Util.isEmpty(acntgYear) || Util.isEmpty(ognzCd)) {
      setList([]);
      return;
    }

    Axios.dataAccess({
      url: "system/bgt/mbsds/dpst/select-bgt-mbsds-ovrl-grnamt-aprv-list.do",
      methodType: "post",
      paramType: "json",
      paramData: {
        acntgYear,
        grnamtOrgCd: ognzCd,
      },
      onSuccessFn: (res: any) => {
        const rows = Array.isArray(res?.result?.list) ? res.result.list : [];

        const mapped: NoticeRow[] = rows.map((r: any) => ({
          acntgYear: String(r.acntgYear),
          acntgMonth: String(r.acntgMonth),
          implDt: toYmd(r.implDt),
          grnamtOrgCd: String(r.grnamtOrgCd),
          bgtSbjct1stCd: String(r.bgtSbjct1stCd),
          bgtSbjct2stCd: String(r.bgtSbjct2stCd),
          bgtSbjct3stCd: String(r.bgtSbjct3stCd),
          bgtSbjct1stNm: String(r.bgtSbjct1stNm),
          bgtSbjct2stNm: String(r.bgtSbjct2stNm),
          bgtSbjct3stNm: String(r.bgtSbjct3stNm),

          aprvAmt: r.aprvAmt,
        }));

        setList(mapped);
      },
    });
  }, [callTime, acntgYear, ognzCd]);

  const unreadCnt = list.length;
  if (unreadCnt <= 0) return null;

  // =========================
  // 확인 버튼 하나: (prefill + 확인처리 + 제거/재조회)
  // =========================
  const handleConfirmAndPick = (row: NoticeRow) => {
    const rowKey = pickKeyFromRow(row);
    if (confirmingKey && isSameKey(confirmingKey, rowKey)) return;

    AppContext.openAlert({
      title: "교부금 입력 확인",
      msg: "교부금 하달 내역이 수입 결의서에 자동 세팅되고 알림은 사라집니다.\n해당 교부금을 입력하시겠습니까?",
      icon: "question",
      closeText: "취소",
      confirmText: "입력",
      handleConfirm: () => {
        // 1) 메인 폼 자동 세팅 (저장 X)
        const dpstDt = Util.unHypen(row.implDt);
        const amount = Util.comma(String(row.aprvAmt ?? 0));
        const summary = `교부금 하달 수입 (${row.bgtSbjct3stNm})`;

        onPick({
          dpstDt,
          kwanCd: row.bgtSbjct1stCd,
          hangCd: row.bgtSbjct2stCd,
          mokCd: row.bgtSbjct3stCd,
          amount,
          summary,
          memo: "",
        });

        // 2) 확인처리 API 호출
        setConfirmingKey(rowKey);
        AppContext.showSpinner();

        Axios.dataAccess({
          url: "system/bgt/mbsds/dpst/save-bgt-mbsds-ovrl-grnamt-aprv.do",
          methodType: "post",
          paramType: "json",
          paramData: {
            acntgYear: row.acntgYear,
            acntgMonth: row.acntgMonth,
            implDt: Util.unHypen(row.implDt),
            grnamtOrgCd: row.grnamtOrgCd,
            bgtSbjct1stCd: row.bgtSbjct1stCd,
            bgtSbjct2stCd: row.bgtSbjct2stCd,
            bgtSbjct3stCd: row.bgtSbjct3stCd,
            idntyYn: "Y",
          },
          onSuccessFn: () => {
            AppContext.hideSpinner();
            setConfirmingKey(null);

            // 3) 알림 즉시 제거 (7개 키로 비교)
            setList((prev) => prev.filter((x) => !isSameKey(pickKeyFromRow(x), rowKey)));

            // 남은게 없으면 닫기 (정확하게 prev 기반으로)
            setOpen((prevOpen) => {
              return prevOpen;
            });

            AppContext.openAlert({
              title: "수입 결의서 입력",
              msg: "교부금 하달 내역이 수입 결의서에 자동 입력되었습니다.",
              icon: "info",
              closeText: "확인",
            });
          },
        });
      },
    });
  };

  return (
    <div className="mt10 mb10">
      <div
        className="p12 bdrd8 bd1 bdcCED4DA dpFlx aiC jcSb csP boxShadowSoft"
        style={{ backgroundColor: "#E8F1FF" }}
        onClick={() => setOpen((v) => !v)}
      >
        <div className="dpFlx aiC gap10">
          <span className="fm-b fs15 fc222222 ls1">교부금 하달 알림</span>
          <span className="fm-c fs13 fc4E4E4E ls1">
            미확인 <b className="fm-b fc222222">{unreadCnt}</b>건 (클릭하여 확인)
          </span>
        </div>

        <div className="dpFlx aiC gap10">
          <span
            className="fm-c fs12 fc555555 tdU-hover csP"
            onClick={(e) => {
              e.stopPropagation();
              setCallTime(new Date());
            }}
          >
            새로고침
          </span>

          <span
            className="fm-b fs14 fc4E4E4E trst01"
            style={{
              display: "inline-block",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            ▼
          </span>
        </div>
      </div>

      {open && (
        <div className="mt8 bd1 bdcDDDDDD bdrd10 ofH">
          <div
            className="dpFlx aiC jcSb bdb1"
            style={{
              borderBottomColor: "rgba(92,147,255,0.18)",
              borderBottomStyle: "solid",
              borderBottomWidth: 1,
              background: "rgba(255,255,255,0.55)",
              height: 8,
            }}
          />

          <div className="p10">
            <div className="dpGrid rg10">
              {list.map((row, idx) => {
                const hovered = hoverIdx === idx;
                const monthLabel = toMonthLabel(row.acntgMonth);

                const rowKey = pickKeyFromRow(row);
                const isConfirming = confirmingKey ? isSameKey(confirmingKey, rowKey) : false;

                return (
                  <div
                    key={`${row.acntgYear}-${row.acntgMonth}-${row.implDt}-${row.grnamtOrgCd}-${row.bgtSbjct1stCd}-${row.bgtSbjct2stCd}-${row.bgtSbjct3stCd}`}
                    className="p12 bdrd10 bd1 trst01 dpFlx aiC jcSb"
                    style={{
                      borderColor: hovered ? "rgba(114, 152, 233, 0.31)" : "rgba(205,212,218,0.70)",
                      background: hovered
                        ? "linear-gradient(135deg, rgba(92,147,255,0.06) 0%, rgba(255,255,255,0.92) 55%, rgba(232,241,255,0.40) 100%)"
                        : "rgba(255,255,255,0.92)",
                      boxShadow: hovered ? "0 8px 16px rgba(30,70,106,0.14)" : "0 4px 10px rgba(0,0,0,0.08)",
                      transform: hovered ? "translateY(-0.5px)" : "translateY(0px)",
                      opacity: isConfirming ? 0.75 : 1,
                    }}
                    onMouseEnter={() => setHoverIdx(idx)}
                    onMouseLeave={() => setHoverIdx(null)}
                  >
                    {/* 좌측 정보 */}
                    <div className="dpFlx flxdCol flx1 ofH">
                      <div className="dpFlx aiC gap10">
                        <span className="fm-b fs13 fc222222">{row.implDt}</span>
                      </div>

                      <div className="fm-c fs13 mt6 ellipsis" style={{ color: "#4E4E4E" }}>
                        {row.bgtSbjct1stNm} <span style={{ color: "#B3B9C2" }}>/</span> {row.bgtSbjct2stNm}{" "}
                        <span style={{ color: "#B3B9C2" }}>/</span> {row.bgtSbjct3stNm}
                      </div>

                      <div className="mt6">
                        <span
                          className="fm-c fs12"
                          style={{
                            color: "#5F6369",
                            backgroundColor: "rgba(0,0,0,0.04)",
                            borderRadius: 8,
                            padding: "4px 8px",
                            display: "inline-block",
                          }}
                        >
                          회계년월 {row.acntgYear}년 {monthLabel}월
                        </span>
                      </div>
                    </div>

                    {/* 우측 액션 */}
                    <div className="dpFlx aiC gap12 flxs0">
                      <div
                        className="dpFlx aiC jcC"
                        style={{
                          minWidth: 120,
                          padding: "8px 12px",
                          borderRadius: 999,
                          border: "1px solid rgba(92,147,255,0.22)",
                          background: "linear-gradient(180deg, rgba(92,147,255,0.10) 0%, rgba(92,147,255,0.06) 100%)",
                        }}
                      >
                        <span className="fm-b fs14" style={{ color: "#112b41" }}>
                          {Util.comma(String(row.aprvAmt ?? 0))}원
                        </span>
                      </div>

                      {/* 확인 버튼(하나) */}
                      <Button
                        width={80}
                        height={36}
                        fontFamily="fm-b"
                        fontSize={13}
                        letterSpacing={-0.6}
                        color={isConfirming ? "#666" : "#fff"}
                        backgroundColor={isConfirming ? "#E9ECEF" : "#2F80ED"}
                        borderRadius={10}
                        buttonClass="dpFlx aiC jcC"
                        buttonName={isConfirming ? "처리중" : "확인"}
                        onClickFunc={(e?: any) => {
                          // Button 컴포넌트가 event를 넘길 수도 있어서 안전하게 stop
                          if (e?.stopPropagation) e.stopPropagation();
                          handleConfirmAndPick(row);
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            className="p12 dpFlx aiC jcFe"
            style={{
              background: "rgba(255,255,255,0.65)",
              borderTop: "1px solid rgba(92,147,255,0.16)",
            }}
          >
            <span className="fm-c fs12 fc5F6369">
              ※ <b className="fm-b fc222222">확인</b>을 누르면 수입 결의서에 자동 세팅되고 알림이 제거됩니다.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrnamtNoticeBox;
